import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CourseAdminApi } from '../../services/api/CourseAdminApi';
import { CourseResourceApi } from '../../services/api/CourseResourceApi';
import CourseInvitePanel from '../../components/courses/CourseInvitePanel';
import CreateResourceWizard from './resources/CreateResourceWizard';
import './CourseAdminDashboard.css';

/**
 * Course-Admin Dashboard.
 *
 * Route: /course/:courseId/admin
 * Access: gated client-side by visiting via a course where the user is owner /
 * course_admin (per-enrollment) / course_admin or org_admin of the course's org.
 * The backend re-checks via @CourseAdminCheck — a user without permission will
 * receive 403s and we render the corresponding empty / error state.
 */
function CourseAdminDashboard() {
  const { courseId } = useParams();
  const [activeTab, setActiveTab] = useState('roster');
  const [roster, setRoster] = useState(null);
  const [progressSummary, setProgressSummary] = useState(null);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadRoster = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await CourseAdminApi.getRoster(courseId);
      if (resp.status === 401 || resp.status === 403) { setAccessDenied(true); return; }
      if (!resp.ok) { setError(`Failed to load roster (${resp.status})`); return; }
      setRoster(await resp.json());
    } catch (e) {
      setError(e?.message || 'Network error loading roster');
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  const loadProgress = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await CourseAdminApi.getProgressSummary(courseId);
      if (resp.status === 401 || resp.status === 403) { setAccessDenied(true); return; }
      if (!resp.ok) { setError(`Failed to load progress (${resp.status})`); return; }
      setProgressSummary(await resp.json());
    } catch (e) {
      setError(e?.message || 'Network error loading progress');
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    if (accessDenied) return;
    if (activeTab === 'roster') loadRoster();
    else if (activeTab === 'progress') loadProgress();
  }, [activeTab, loadRoster, loadProgress, accessDenied]);

  if (accessDenied) {
    return (
      <div className="course-admin-dashboard">
        <header className="course-admin-header">
          <div>
            <h1>Course Admin</h1>
            <Link to={`/course/${courseId}`} className="course-admin-back-link">← Back to course</Link>
          </div>
        </header>
        <section className="course-admin-content">
          <div className="course-admin-access-denied" role="alert" style={{ padding: 24, border: '1px solid #feb2b2', borderRadius: 8, background: '#fff5f5' }}>
            <h2 style={{ marginTop: 0 }}>Access denied</h2>
            <p>You do not have permission to administer this course. This page is restricted to the course owner, designated course admins, or administrators of the course's organization.</p>
            <p>
              <Link to={`/course/${courseId}`}>Return to course</Link>
              {' · '}
              <Link to="/dashboard">Go to dashboard</Link>
            </p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="course-admin-dashboard">
      <header className="course-admin-header">
        <div>
          <h1>Course Admin</h1>
          <Link to={`/course/${courseId}`} className="course-admin-back-link">← Back to course</Link>
        </div>
      </header>

      <nav className="course-admin-tabs" role="tablist" aria-label="Course admin sections">
        {['roster', 'progress', 'resources'].map(tab => (
          <button
            key={tab}
            role="tab"
            aria-selected={activeTab === tab}
            className={`course-admin-tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'roster' && 'Roster'}
            {tab === 'progress' && 'Progress'}
            {tab === 'resources' && 'Resources'}
          </button>
        ))}
      </nav>

      <section className="course-admin-content">
        {loading && <div className="course-admin-loading">Loading…</div>}
        {error && <div className="course-admin-error" role="alert">{error}</div>}

        {!loading && !error && activeTab === 'roster' && (
          <>
            <CourseInvitePanel
              endeavorId={courseId}
              onRosterChanged={loadRoster}
            />
            <RosterPanel roster={roster} />
          </>
        )}
        {!loading && !error && activeTab === 'progress' && (
          <ProgressPanel summary={progressSummary} />
        )}
        {!loading && !error && activeTab === 'resources' && (
          <ResourcesPanel courseId={courseId} />
        )}
      </section>
    </div>
  );
}

function RosterPanel({ roster }) {
  if (!roster) return <div className="course-admin-empty">No roster data.</div>;
  // Backend currently returns a List<Enrollment>-like structure.
  const rows = Array.isArray(roster) ? roster : (roster.enrollments || roster.members || []);
  if (rows.length === 0) return <div className="course-admin-empty">No enrolled users yet.</div>;
  return (
    <table className="course-admin-table">
      <thead>
        <tr><th>User</th><th>Email</th><th>Role</th><th>Status</th></tr>
      </thead>
      <tbody>
        {rows.map((r, i) => {
          const composedName = [r.firstName, r.lastName].filter(Boolean).join(' ').trim();
          const displayName = r.userName
            || (composedName || null)
            || r.userEmail
            || r.email
            || `User ${r.userId}`;
          const displayEmail = r.userEmail || r.email || '—';
          return (
            <tr key={r.enrollmentId || r.userId || i}>
              <td>{displayName}</td>
              <td>{displayEmail}</td>
              <td>{r.role || '—'}</td>
              <td>{r.status || '—'}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function ProgressPanel({ summary }) {
  if (!summary) return <div className="course-admin-empty">No progress data.</div>;
  return (
    <pre className="course-admin-pre">{JSON.stringify(summary, null, 2)}</pre>
  );
}

const RESOURCE_KIND_LABELS = {
  quiz: 'Quiz',
  flashcard_deck: 'Flashcard Deck',
  audio: 'Audio',
};

function ResourcesPanel({ courseId }) {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await CourseResourceApi.list(courseId);
      if (!resp.ok) { setError(`Failed to load resources (${resp.status})`); return; }
      const data = await resp.json();
      setResources(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.message || 'Network error loading resources');
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => { reload(); }, [reload]);

  const onDelete = async (resourceId) => {
    setError(null);
    if (!window.confirm('Delete this resource? Students will no longer see it.')) return;
    const resp = await CourseResourceApi.remove(courseId, resourceId);
    if (!resp.ok) {
      setError(`Failed to delete resource (${resp.status})`);
      return;
    }
    reload();
  };

  const openCreate = () => { setEditing(null); setWizardOpen(true); };
  const openEdit = (r) => { setEditing(r); setWizardOpen(true); };

  if (loading) return <div className="course-admin-loading">Loading resources…</div>;

  return (
    <div>
      {error && <div className="course-admin-error" role="alert">{error}</div>}

      <div className="course-admin-resources-toolbar">
        <button onClick={openCreate}>+ Add Resource</button>
      </div>

      <CreateResourceWizard
        open={wizardOpen}
        endeavorId={courseId}
        editing={editing}
        onClose={() => { setWizardOpen(false); setEditing(null); }}
        onSaved={reload}
      />

      {resources.length === 0 ? (
        <div className="course-admin-empty">No resources yet.</div>
      ) : (
        <table className="course-admin-table">
          <thead><tr><th>Title</th><th>Kind</th><th>Visibility</th><th>Created</th><th></th></tr></thead>
          <tbody>
            {resources.map(r => (
              <tr key={r.resourceId}>
                <td>{r.title}</td>
                <td>{RESOURCE_KIND_LABELS[r.kind] || r.kind}</td>
                <td>{r.visibility}</td>
                <td>{r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '—'}</td>
                <td>
                  <button className="course-admin-edit-btn" onClick={() => openEdit(r)} style={{ marginRight: 8 }}>Edit</button>
                  <button className="course-admin-delete-btn" onClick={() => onDelete(r.resourceId)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default CourseAdminDashboard;
